import React from 'react';
import { useApolloClient, type ObservableQuery } from '@apollo/client';

import {
  AmsLogStatus,
  ClientArtifactsSentEmailsDocument,
  type ClientArtifactsSentEmailsQuery,
  type ClientArtifactsSentEmailsQueryVariables
} from '~/api/graphql';
import { amsLogPollingRemainingMs } from '~/components/ams-email-log/polling';

const BATCH_SIZE = 50;
const LOG_POLL_MS = 2_000;

export type ClientArtifactSentEmail =
  ClientArtifactsSentEmailsQuery['latestSentOutboundEmails'][number];
export type ArtifactSentEmailState = { email: ClientArtifactSentEmail | null; isUnread: boolean };
type Batch = {
  ids: string[];
  subscription: ReturnType<ObservableQuery<ClientArtifactsSentEmailsQuery>['subscribe']>;
};

export const useClientArtifactSentEmails = (artifactIds: string[], enabled: boolean) => {
  const client = useApolloClient();
  const idsKey = JSON.stringify(enabled ? [...new Set(artifactIds)].sort() : []);
  const activeIds = React.useRef(new Set<string>());
  const batches = React.useRef<Batch[]>([]);
  const [states, setStates] = React.useState<Record<string, ArtifactSentEmailState>>({});

  const storeAnswer = React.useCallback((ids: string[], data?: ClientArtifactsSentEmailsQuery) => {
    const byArtifact = new Map(
      data?.latestSentOutboundEmails.map((email) => [email.workspaceArtifactId, email])
    );
    setStates((previous) => {
      const next = { ...previous };
      for (const id of ids) {
        if (!activeIds.current.has(id)) continue;
        next[id] = data
          ? { email: byArtifact.get(id) ?? null, isUnread: false }
          : { email: previous[id]?.email ?? null, isUnread: true };
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    const ids = JSON.parse(idsKey) as string[];
    activeIds.current = new Set(ids);
    const retained: Batch[] = [];
    for (const batch of batches.current) {
      if (batch.ids.some((id) => !activeIds.current.has(id))) batch.subscription.unsubscribe();
      else retained.push(batch);
    }
    batches.current = retained;
    const requested = new Set(retained.flatMap((batch) => batch.ids));
    const missing = ids.filter((id) => !requested.has(id));
    for (let offset = 0; offset < missing.length; offset += BATCH_SIZE) {
      const batchIds = missing.slice(offset, offset + BATCH_SIZE);
      const observable = client.watchQuery<
        ClientArtifactsSentEmailsQuery,
        ClientArtifactsSentEmailsQueryVariables
      >({
        query: ClientArtifactsSentEmailsDocument,
        variables: { workspaceArtifactIds: batchIds },
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'none'
      });
      const subscription = observable.subscribe({
        next: (answer) => {
          if (answer.data?.latestSentOutboundEmails) storeAnswer(batchIds, answer.data);
        },
        error: () => storeAnswer(batchIds)
      });
      batches.current.push({ ids: batchIds, subscription });
    }
    setStates((previous) =>
      Object.fromEntries(Object.entries(previous).filter(([id]) => activeIds.current.has(id)))
    );
  }, [client, idsKey, storeAnswer]);

  React.useEffect(
    () => () => {
      activeIds.current.clear();
      for (const batch of batches.current) batch.subscription.unsubscribe();
      batches.current = [];
    },
    []
  );

  const refresh = React.useCallback(
    async (ids: string[]) => {
      const current = [...new Set(ids)].filter((id) => activeIds.current.has(id));
      for (let offset = 0; offset < current.length; offset += BATCH_SIZE) {
        const batchIds = current.slice(offset, offset + BATCH_SIZE);
        try {
          const answer = await client.query<
            ClientArtifactsSentEmailsQuery,
            ClientArtifactsSentEmailsQueryVariables
          >({
            query: ClientArtifactsSentEmailsDocument,
            variables: { workspaceArtifactIds: batchIds },
            fetchPolicy: 'network-only',
            errorPolicy: 'none'
          });
          storeAnswer(batchIds, answer.data);
        } catch {
          storeAnswer(batchIds);
        }
      }
    },
    [client, storeAnswer]
  );

  const pendingKey = JSON.stringify(
    Object.entries(states)
      .filter(
        ([id, state]) =>
          activeIds.current.has(id) && state.email?.amsLog?.status === AmsLogStatus.Logging
      )
      .map(([id, state]) => [id, state.email?.sentAt])
  );

  React.useEffect(() => {
    const pending = JSON.parse(pendingKey) as [string, string | null][];
    if (!pending.length) return;
    const refreshPending = () => {
      if (document.visibilityState !== 'hidden') void refresh(pending.map(([id]) => id));
    };
    window.addEventListener('focus', refreshPending);
    document.addEventListener('visibilitychange', refreshPending);
    const nowMs = Date.now();
    const remaining = pending.map(([id, sentAt]) => ({
      id,
      until: nowMs + amsLogPollingRemainingMs(AmsLogStatus.Logging, sentAt, nowMs)
    }));
    const remainingMs = Math.max(...remaining.map(({ until }) => until - nowMs));
    let interval: number | undefined;
    let timer: number | undefined;
    if (remainingMs > 0) {
      interval = window.setInterval(() => {
        if (document.visibilityState !== 'hidden')
          void refresh(remaining.filter(({ until }) => until > Date.now()).map(({ id }) => id));
      }, LOG_POLL_MS);
      timer = window.setTimeout(() => window.clearInterval(interval), remainingMs);
    }
    return () => {
      window.removeEventListener('focus', refreshPending);
      document.removeEventListener('visibilitychange', refreshPending);
      window.clearInterval(interval);
      window.clearTimeout(timer);
    };
  }, [pendingKey, refresh]);

  return { states, refresh };
};
